import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// PIN temporaneo richiesto: sostituire quando verrà definita la gestione definitiva.
const EXIT_PIN = '1234';
const ExitProtectionContext = createContext(null);
const KLARO_ROUTES = new Set([
  '/dashboard', '/carica-merce', '/ricette', '/batch', '/magazzino', '/etichette',
  '/etichette/stampa', '/scadenzario', '/opzioni', '/fornitori', '/ingredienti',
  '/allergeni', '/registro', '/ocr', '/lots', '/labels', '/ingredients',
  '/suppliers', '/recipes', '/batches',
]);

function isKlaroRoute(pathname) {
  return KLARO_ROUTES.has(pathname);
}

async function performExitAction(action) {
  if (!action) return;
  try { await action(); }
  catch { toast.error('Uscita da Klaro non completata. Riprova.'); }
}

export function ExitProtectionProvider({ children }) {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    Boolean(user && isKlaroRoute(currentLocation.pathname) && !isKlaroRoute(nextLocation.pathname))
  ));
  const { state: blockerState, proceed: proceedBlockedNavigation, reset: resetBlockedNavigation } = blocker;

  useEffect(() => {
    if (blockerState !== 'blocked') return;
    setPendingAction(null);
    setPin('');
    setError('');
    setDialogOpen(true);
  }, [blockerState]);

  const requestExit = useCallback((action) => {
    setPendingAction(() => action || null);
    setPin('');
    setError('');
    setDialogOpen(true);
  }, []);

  const cancelExit = useCallback(() => {
    setDialogOpen(false);
    setPin('');
    setError('');
    setPendingAction(null);
    if (blockerState === 'blocked') resetBlockedNavigation();
  }, [blockerState, resetBlockedNavigation]);

  const finishExit = useCallback(async () => {
    const action = pendingAction;
    setDialogOpen(false);
    setPin('');
    setError('');
    setPendingAction(null);
    if (blockerState === 'blocked') proceedBlockedNavigation();
    else await performExitAction(action);
  }, [blockerState, proceedBlockedNavigation, pendingAction]);

  const submitPin = async (event) => {
    event.preventDefault();
    if (pin.length !== EXIT_PIN.length || busy) return;
    setBusy(true);
    setError('');
    if (pin === EXIT_PIN) {
      try { await finishExit(); }
      finally { setBusy(false); }
      return;
    }
    setError('PIN non corretto. Riprova.');
    setPin('');
    setBusy(false);
  };

  const value = useMemo(() => ({ requestExit }), [requestExit]);
  return (
    <ExitProtectionContext.Provider value={value}>
      {children}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open && !busy) cancelExit(); }}>
        <DialogContent className="max-w-sm">
          <form onSubmit={submitPin} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Conferma uscita</DialogTitle>
              <DialogDescription>Inserisci il PIN per uscire dall’area Klaro.</DialogDescription>
            </DialogHeader>
            <div>
              <Label htmlFor="klaro-exit-pin">PIN di 4 cifre</Label>
              <Input id="klaro-exit-pin" type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={4} value={pin} onChange={event => setPin(event.target.value.replace(/\D/g, '').slice(0, 4))} className="mt-1.5 text-center text-xl tracking-[.35em]" autoFocus />
              {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={cancelExit} disabled={busy}>Resta in Klaro</Button>
              <Button type="submit" disabled={busy || pin.length !== 4}>{busy ? 'Verifico…' : 'Conferma uscita'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ExitProtectionContext.Provider>
  );
}

export function useExitProtection() {
  const context = useContext(ExitProtectionContext);
  if (!context) throw new Error('useExitProtection must be used within ExitProtectionProvider');
  return context;
}
